<template>
  <div ref="configureTab">
    <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.optionRangeRequireable)">
      <panel-item>
        <label :for="selectedObjectModelProperty.idForProp('selection_type')">
          Selection Type
          <property-change-detector
            :left="selectedObjectModelProperty"
            :right="selectedObjectModelProperty.publishedEquivalent"
            :properties="[['selection_type', 'type']]"
          /> </label>
        <a-select
          v-model="selectedObjectModelProperty.selection_type"
          :options="selectionTypeOptions"
          :id="selectedObjectModelProperty.idForProp('selection_type')"
          compress
        ></a-select>

      </panel-item>
    </panel-block>

    <requireable
      v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.requireable)"
      :model="selectedObjectModelProperty"
      :is-option-range-requireable="selectedObjectModelProperty.can(ObjectModelPropertyCapability.optionRangeRequireable) && selectedObjectModelProperty.selection_type === PickListSelectionType.MultiSelect"
      :min="selectedObjectModelProperty.objectModelPropertiesArray.length ? 1 : 0"
      :max="selectedObjectModelProperty.objectModelPropertiesArray.length"
    >
      <template slot="option-range-label">
        Value Selection Limit
        <property-change-detector
          :left="selectedObjectModelProperty"
          :right="selectedObjectModelProperty.publishedEquivalent"
          :properties="[['option_range_type', 'type'], ['checkbox_range_min', 'min'], ['checkbox_range_max', 'max']]"
        />
      </template>
    </requireable>

    <input-format-config v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.formattable)"
                         :key="selectedObjectModelProperty.client_id"></input-format-config>

    <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.lengthable)" id="pn-char-limit">
      <panel-title>
        Character Limit
        <property-change-detector
          :left="selectedObjectModelProperty"
          :right="selectedObjectModelProperty.publishedEquivalent"
          :properties="[['character_count.min', 'min'], ['character_count.max', 'max']]"
        />
      </panel-title>
      <panel-item>
        <multi-input compress>
          <column>
            <number-input
              id="character_count_min"
              v-model="selectedObjectModelProperty.character_count.min"
              placeholder="No Min"
            ></number-input>
          </column>
          <column>
            <number-input
              v-model="selectedObjectModelProperty.character_count.max"
              placeholder="No Max"
            ></number-input>
          </column>
        </multi-input>
        <template
          v-if="NumberHelper.anyIntegers([selectedObjectModelProperty.character_count.min, selectedObjectModelProperty.character_count.max])">
          <div>
            <a-select
              v-model="selectedObjectModelProperty.character_count.show_char_limit"
              :options="charLimitOptions"
              id="show-char-limit"
              compress
            ></a-select>
          </div>
          <row>
            <column>
              <div class="sr-only">
                <label :for="selectedObjectModelProperty.idForProp('char_limit_message')">Custom Validation Error</label>
              </div>
              <expandable-textarea
                v-model="selectedObjectModelProperty.character_count.char_limit_message"
                :id="selectedObjectModelProperty.idForProp('char_limit_message')"
                placeholder="Custom Validation Error"
              ></expandable-textarea>
            </column>
          </row>
        </template>
      </panel-item>
    </panel-block>

    <date-min-maxable
      v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.dateMinMaxable)"
      :model="selectedObjectModelProperty"
    >
      <template #modeLabel>
        <property-change-detector
          :left="selectedObjectModelProperty"
          :right="selectedObjectModelProperty.publishedEquivalent"
          :properties="[['datetime_mode']]"
        />
      </template>
      <template #timeFormatLabel>
        <property-change-detector
          :left="selectedObjectModelProperty"
          :right="selectedObjectModelProperty.publishedEquivalent"
          :properties="[['time_format']]"
        />
      </template>
      <template #minLabel>
        <property-change-detector
          :left="selectedObjectModelProperty"
          :right="selectedObjectModelProperty.publishedEquivalent"
          :properties="[['min_datetime_type', 'type'], [formattedMinDatetimeFunction, 'value']]"
        />
      </template>
      <template #maxLabel>
        <property-change-detector
          :left="selectedObjectModelProperty"
          :right="selectedObjectModelProperty.publishedEquivalent"
          :properties="[['max_datetime_type', 'type'], [formattedMaxDatetimeFunction, 'value']]"
        />
      </template>
    </date-min-maxable>

    <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.autoIncrementable)" id="pn-autoincrement">
      <panel-title>
        First Number
        <help-tooltip text="This will be the first number in the sequence of in the auto-incremented field. If you set a number is already used by an object of the same model, the system will start at an unused number."></help-tooltip>
      </panel-title>

      <panel-item>
        <number-input
          v-model="selectedObjectModelProperty.increment_start"
          placeholder=""
          id="increment_start"
          :min="0"
          name="increment_start"
        ></number-input>
      </panel-item>
    </panel-block>

    <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.autoIncrementable)">
      <panel-title>
        Resetting Numbering on Date
        <help-tooltip text="This will allow the sequence to reset back to the first number at the start of a new calendar year (Jan 1st), or Maryland fiscal year (July 1st), or within every month throughout. The date will be appended to the number as a prefix."></help-tooltip>
      </panel-title>

      <panel-item>
        <a-select
          id="increment_reset_type"
          :options="incResetOptions"
          v-model="selectedObjectModelProperty.increment_reset_type"
          compress
        >
        </a-select>

        <p v-if="autoIncrementExample">
          Example: {{ autoIncrementExample }}
        </p>
      </panel-item>
    </panel-block>

    <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.derivedValuable)">
      <panel-title>Create Formula</panel-title>
      <panel-item>
        <formula-wrapper
          field-name="derived_value"
          :dynamic-options="dynamicOptions"
          serverside-warning-tooltip
        >
          <template #value="slotProps">
            <expandable-textarea
              v-model="slotProps.model[slotProps.fieldName]"
              id="formulaValue"
              type="text"
              placeholder="Enter a formula"
              is-multiline
            ></expandable-textarea>
          </template>
        </formula-wrapper>
      </panel-item>
    </panel-block>

    <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.fileAttachable)">
        <panel-item>
          <label :for="selectedObjectModelProperty.idForProp('file_attachment_type')">
            Upload Mode
            <property-change-detector
              :left="selectedObjectModelProperty"
              :right="selectedObjectModelProperty.publishedEquivalent"
              :properties="[['file_attachment_type', 'type']]"
            /> </label>
          <a-select
            v-model="selectedObjectModelProperty.file_attachment_type"
            :options="fileAttachmentOptions"
            :id="selectedObjectModelProperty.idForProp('file_attachment_type')"
            compress
          ></a-select>
        </panel-item>
    </panel-block>

    <security-maskable-config v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.securityMaskable)"
                              determinant-field-name="security_maskable_enabled"
                              :model="selectedObjectModelProperty"
                              :v-option-sets="securityMaskableViewersVOptionSets"
    ></security-maskable-config>

    <panel-block v-if="selectedObjectModelProperty.can(ObjectModelPropertyCapability.loggable)">
      <panel-item>
        <row>
          <column>
            <label :for="selectedObjectModel.idForProp('is_logged')">Capture in Change Log?</label>
          </column>
          <suffix>
            <help-tooltip text="To track changes to this property select 'Yes'"></help-tooltip>
          </suffix>
        </row>
        <toggle-select
          :id="selectedObjectModel.idForProp('is_logged')"
          compress
          v-model="selectedObjectModelProperty.is_logged"
        ></toggle-select>
      </panel-item>
    </panel-block>
  </div>
</template>

<script lang="ts">
  import Vue from "vue";
  import {ObjectModelPropertyCapability} from "~/models/objectModelProperties/objectModelPropertyCapability";
  import selectedMixin from "../mixins/selectedMixin";
  import InputFormatConfig from "./input-format-config.vue"
  import {IDefaultVOption} from "~/interfaces/vSelectInterfaces";
  import {NumberHelper} from "~/helpers/numberHelper";
  import Requireable from "../../../common/components/modelProperties/requireable.vue";
  import DateMinMaxable from "~/components/date-min-maxable.vue";
  import FormulaWrapper from "../../../common/components/formulas/formulaWrapper.vue";
  import ExpandableTextarea from "../../../common/components/expandableTextarea.vue";
  import {IDynamicFieldsOptions} from "~/interfaces/dynamicFieldsInterfaces/dynamicOptionsInterfaces";
  import ObjectModelPropertyDynamicFieldsHandler from "../../../common/models/dynamicFields/handlers/objectModelPropertyDynamicFieldsHandler";
  import PropertyChangeDetector from "./property-change-detector.vue"
  import DatetimeHelper from '../../../common/helpers/datetimeHelper';
  import {PickListSelectionType} from "~/enums/pickListSelectionType";
  import {FileAttachmentType} from "~/enums/fileAttachmentType";
  import HandlerOfHandlers from "~/models/dynamicFields/handlers/handlerOfHandlers";
  import ObjectModelRecordMetadataDynamicFieldsHandler from "~/models/dynamicFields/handlers/objectModelRecordMetadataDynamicFieldsHandler";
  import SecurityMaskableConfig from "~/components/configuration/security-maskable-config.vue"
  import permitteeVOptionSetMixin from "~/mixins/permitteeVOptionSetMixin";
  import {IncrementalResetTypeVOptions, IncrementalResetType} from "~/enums/incrementalResetType";
  import AppGroupPermitteeSearchMixin from '~/mixins/appGroupPermitteeSearchMixin';

  export default Vue.extend({
    components: {
      InputFormatConfig,
      Requireable,
      DateMinMaxable,
      FormulaWrapper,
      ExpandableTextarea,
      PropertyChangeDetector,
      SecurityMaskableConfig,
    },
    mixins: [
      selectedMixin,
      permitteeVOptionSetMixin,
      AppGroupPermitteeSearchMixin,
    ],
    data: function () {
      return {
        ObjectModelPropertyCapability,
        NumberHelper,
        DatetimeHelper,
        PickListSelectionType,
        IncrementalResetType,
        securityMaskableViewersVOptionSets: [],
      };
    },
    computed: {
      application_id(): string {
        return this.selectedObjectModel.application_id;
      },
      charLimitOptions(): IDefaultVOption[] {
        return [
          {label: 'Display character limit', value: true},
          {label: 'Hide character limit', value: false}
        ];
      },
      incResetOptions(): IDefaultVOption[] {
        return IncrementalResetTypeVOptions;
      },
      dynamicOptions(): IDynamicFieldsOptions[] {
        return [
          {
            is: 'dynamic-fields-tree',
            props: {
              objectModel: this.selectedObjectModel,
              parent: this.selectedObjectModelProperty,
              fieldsHandler: new HandlerOfHandlers([
                  {
                    displayName: 'About Record',
                    handler: new ObjectModelRecordMetadataDynamicFieldsHandler(),
                  },
                  {
                    displayName: 'Object Model Properties',
                    handler: new ObjectModelPropertyDynamicFieldsHandler({
                      mode: 'flat',
                      objectModel: this.selectedObjectModel,
                      excluded_property_client_ids: [this.selectedObjectModelProperty.client_id]
                    })
                  }
                ]
              ),
            },
          },
        ];
      },
      formattedMinDatetimeFunction() {
        return m => {
          if (m) {
            // since full date and time are always stored (even when user only wants time) must compare after formatted
            return DatetimeHelper.formatDate(DatetimeHelper.parseDate(m.min_datetime), DatetimeHelper.timezone, DatetimeHelper.displayFormat(m.datetime_mode))
          }
        }
      },
      formattedMaxDatetimeFunction() {
        return m => {
          if (m) {
            // since full date and time are always stored (even when user only wants time) must compare after formatted
            return DatetimeHelper.formatDate(DatetimeHelper.parseDate(m.max_datetime), DatetimeHelper.timezone, DatetimeHelper.displayFormat(m.datetime_mode))
          }
        }
      },
      selectionTypeOptions(): IDefaultVOption[] {
        return [
          {label: 'Single Select', value: PickListSelectionType.SingleSelect},
          {label: 'Multiple Select', value: PickListSelectionType.MultiSelect},
        ];
      },
      fileAttachmentOptions(): IDefaultVOption[] {
        return [
          {label: 'Single File', value: FileAttachmentType.SingleFile},
          {label: 'Multiple Files', value: FileAttachmentType.MultipleFiles}
        ]
      },
      autoIncrementExample(): string {
        if (!this.selectedObjectModelProperty.increment_reset_type) return null
        if (this.selectedObjectModelProperty.increment_reset_type === IncrementalResetType.DoNotReset) return null

        const firstNumber = this.selectedObjectModelProperty.increment_start?.toString() || '1'

        const now = new Date()
        const year = now.getFullYear().toString()
        const fiscalYear = ((now.getMonth() + 1) > 7) ? (now.getFullYear() + 1).toString() : year
        // 0 = January
        const month = (now.getMonth() + 1).toString().padStart(2, '0')
        const day = now.getDate().toString().padStart(2, '0')

        switch (this.selectedObjectModelProperty.increment_reset_type) {
          case IncrementalResetType.ResetEveryCalendarYear:
            return year + firstNumber
          case IncrementalResetType.ResetEveryMonthInCalendarYear:
            return year + month + firstNumber
          case IncrementalResetType.ResetEveryDayInCalendarYear:
            return year + month + day + firstNumber
          case IncrementalResetType.ResetEveryFiscalYear:
            return fiscalYear + firstNumber
          case IncrementalResetType.ResetEveryMonthInFiscalYear:
            return fiscalYear + month + firstNumber
          case IncrementalResetType.ResetEveryDayInFiscalYear:
            return fiscalYear + month + day + firstNumber
          default:
            return 'unhandled'
        }
      },
    },
    methods: {
      updateAppGroupVOptionSets(): void {
        this.securityMaskableViewersVOptionSets = this.permitteeSecurityMaskableObjectModelViewersVOptionSets;
      },
    },
  });
</script>
